import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiBriefcase, FiCheckCircle } from 'react-icons/fi';
import { settingsService } from '../services/settingsService';
import { useClient } from '../context/ClientContext';

export default function WorkspaceSetup() {
  const navigate = useNavigate();
  const { profile, updateOnboardingStatus } = useClient();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!name.trim()) {
      setError('Enter your firm, business, or company name.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await settingsService.completePlatformOnboarding({
        name: name.trim(),
        plan: 'free',
        role: profile?.user_type || 'business_owner'
      });

      await updateOnboardingStatus(true);
      navigate('/dashboard', { replace: true });
    } catch (submitError) {
      setError(submitError.message || 'Unable to create your workspace.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-5%] left-[-5%] w-80 h-80 bg-blue-600/10 rounded-full blur-[100px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl bg-white rounded-[32px] shadow-2xl border border-slate-100 p-8 md:p-12 relative z-10"
      >
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <SafeIcon icon={FiBriefcase} className="text-white text-2xl" />
          </div>
        </div>

        <div className="text-center mb-10">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">
            One quick step
          </p>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-3">
            Name Your Workspace
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed">
            Tell us the name of your firm, business, or company. You can finish
            your profile details later.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-700 text-sm font-bold">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Firm, Business, or Company Name
            </label>
            <input
              autoFocus
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. ABC Accounting Firm"
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
            />
          </div>

          <div className="p-5 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-start gap-3">
            <SafeIcon icon={FiCheckCircle} className="text-emerald-600 text-lg shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-800 leading-relaxed font-bold">
              Your workspace will start with free access. You can add details,
              invite teammates, and configure your accounting experience later.
            </p>
          </div>

          <button
            disabled={isSaving}
            className="w-full py-5 bg-slate-900 hover:bg-black text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl disabled:opacity-70 flex items-center justify-center gap-3"
          >
            {isSaving ? 'Creating Workspace...' : 'Continue to ProfitYo'}
            {!isSaving && <SafeIcon icon={FiArrowRight} />}
          </button>
        </form>
      </motion.div>
    </div>
  );
}